import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import Loader from "../Loader";
import useGetTrialDistinctForms from "../../hooks/useGetTrialDistinctForms";
import useGetHistogramConfig from "../../hooks/useGetHistogramConfig";
import useUpdateHistogramConfigMutation from "../../hooks/useUpdateHistogramConfigMutation";

/**
 * Builds the initial local state from the saved config.
 * Returns: { ecrf_<id>: { checked: bool, questions: Set<string> }, advanced_survey_<id>: ... }
 */
const buildInitialState = (savedSelections) => {
  const state = {};
  for (const sel of savedSelections) {
    const key = sel.form_type === "ecrf" ? `ecrf_${sel.form_id}` : `advanced_survey_${sel.form_id}`;
    state[key] = {
      checked: true,
      questions: new Set(sel.selected_questions),
    };
  }
  return state;
};

const FormSection = ({ title, forms, formKeyPrefix, selections, onChange }) => {
  if (forms.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {forms.map((form) => {
        const key = `${formKeyPrefix}_${form.id}`;
        const formState = selections[key] ?? { checked: false, questions: new Set() };

        return (
          <Box key={form.id} sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formState.checked}
                  onChange={(e) => onChange(key, "form", e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {form.name}
                </Typography>
              }
            />

            {formState.checked && form.numeric_questions.length > 0 && (
              <Box sx={{ pl: 4 }}>
                {form.numeric_questions.map((q) => (
                  <FormControlLabel
                    key={q.name}
                    control={
                      <Checkbox
                        checked={formState.questions.has(q.name)}
                        onChange={(e) => onChange(key, "question", e.target.checked, q.name)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        {q.title}
                      </Typography>
                    }
                  />
                ))}
              </Box>
            )}

            {formState.checked && form.numeric_questions.length === 0 && (
              <Box sx={{ pl: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No numeric questions found in this form.
                </Typography>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const SurveyHistogramConfig = ({ trialId }) => {
  const { data: formsData, isLoading: formsLoading } = useGetTrialDistinctForms({
    trialId,
    enabled: Boolean(trialId),
  });
  const { data: configData, isLoading: configLoading } = useGetHistogramConfig({
    trialId,
    enabled: Boolean(trialId),
  });

  const { mutate: saveConfig, isLoading: isSaving } = useUpdateHistogramConfigMutation();

  const [selections, setSelections] = useState({});

  // Initialise local state once saved config is loaded
  useEffect(() => {
    if (configData?.selections) {
      setSelections(buildInitialState(configData.selections));
    }
  }, [configData]);

  const handleChange = (formKey, type, checked, questionName) => {
    setSelections((prev) => {
      const current = prev[formKey] ?? { checked: false, questions: new Set() };

      if (type === "form") {
        return {
          ...prev,
          [formKey]: { ...current, checked },
        };
      }

      // type === "question"
      const nextQuestions = new Set(current.questions);
      if (checked) nextQuestions.add(questionName);
      else nextQuestions.delete(questionName);

      return {
        ...prev,
        [formKey]: { ...current, questions: nextQuestions },
      };
    });
  };

  const handleSave = () => {
    const payload = [];

    for (const [key, state] of Object.entries(selections)) {
      if (!state.checked) continue;

      if (key.startsWith("ecrf_")) {
        payload.push({
          ecrf_id: parseInt(key.replace("ecrf_", ""), 10),
          questions: [...state.questions],
        });
      } else {
        payload.push({
          advanced_survey_id: parseInt(key.replace("advanced_survey_", ""), 10),
          questions: [...state.questions],
        });
      }
    }

    saveConfig({ trialId, selections: payload });
  };

  if (formsLoading || configLoading) return <Loader />;

  const ecrfs = formsData?.ecrfs ?? [];
  const advancedSurveys = formsData?.advanced_surveys ?? [];

  if (ecrfs.length === 0 && advancedSurveys.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No eCRFs or ePRO forms are assigned to any visit in this trial.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <FormSection
        title="eCRFs"
        forms={ecrfs}
        formKeyPrefix="ecrf"
        selections={selections}
        onChange={handleChange}
      />

      {ecrfs.length > 0 && advancedSurveys.length > 0 && <Divider sx={{ my: 2 }} />}

      <FormSection
        title="ePRO"
        forms={advancedSurveys}
        formKeyPrefix="advanced_survey"
        selections={selections}
        onChange={handleChange}
      />

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" size="small" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Configuration"}
        </Button>
      </Box>
    </Box>
  );
};

export default SurveyHistogramConfig;
